import { forwardRef } from "react";

/**
 * A text input that follows this user's UI/UX standard:
 *  - rule 1: Escape clears the value before it does anything else; a second Escape (on an
 *    already-empty field) propagates normally.
 *  - rule 8: an in-field X on the right clears the value and leaves focus in the field, running
 *    the same `onChange("")` path as typing so debounce/reset logic still fires.
 *
 * `onChange` is the string value, not the event -- callers here never need the raw event.
 */
export interface ClearableInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "onChange" | "value"> {
  value: string;
  onChange: (value: string) => void;
}

export const ClearableInput = forwardRef<HTMLInputElement, ClearableInputProps>(
  function ClearableInput({ value, onChange, onKeyDown, disabled, readOnly, ...rest }, ref) {
    const canClear = value.length > 0 && !disabled && !readOnly;

    return (
      <span className="clearable">
        <input
          {...rest}
          ref={ref}
          value={value}
          disabled={disabled}
          readOnly={readOnly}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Escape" && value.length > 0) {
              e.preventDefault();
              e.stopPropagation();
              onChange("");
              return;
            }
            onKeyDown?.(e);
          }}
        />
        {canClear && (
          <button
            type="button"
            className="clearable-x"
            aria-label="Clear"
            tabIndex={-1}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => onChange("")}
          >
            &times;
          </button>
        )}
      </span>
    );
  },
);
